import PQueue from 'p-queue'
import EventEmitter from 'eventemitter3'
import type {
  AICallEvent,
  BalanceInfo,
  DemoProject,
  ResponseFormat,
  StorageOptions,
  SubscribeDevClientConfig,
  SubscriptionStatus,
  UploadFileOptions,
  UploadUrlResponse,
  UsageLimits,
  UserStorage,
} from './types'
import {
  AccessDeniedError,
  AuthenticationError,
  InsufficientBalanceError,
  NotFoundError,
  RateLimitError,
  SubscribeDevError,
  UnsubscribedError,
  ValidationError,
  PlanUpgradeRequiredError,
  WorkflowUnavailableError
} from './errors'
import { z } from 'zod'
import { StreamableResponse } from './streamable-response'

/**
 * Generate unique request ID for tracking AI calls
 */
function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * SubscribeDevClient - Drop-in replacement for Replicate.AI and OpenRouter with end-user-billing and rate limiting
 */
export class SubscribeDevClient extends EventEmitter {
  // Marker to identify local client with events
  public readonly __LOCAL_CLIENT_WITH_EVENTS__ = true

  private readonly _config: SubscribeDevClientConfig & {
    baseUrl: string
    authorizationUrl: string
    timeout: number
    maxRetries: number
    debug: boolean
  }
  private readonly queue: PQueue // Used for rate limiting requests
  private readonly fetch: typeof fetch

  constructor(config: SubscribeDevClientConfig) {
    super()
    
    // Authentication is optional - client can be used for getting authorizationUrl even without tokens
    const baseUrl = config.baseUrl || 'https://api.subscribe.dev';
    // Set defaults
    this._config = {
      baseUrl,
      authorizationUrl: config.authorizationUrl || baseUrl.replace("api", "auth"),
      timeout: config.timeout || 600000, // 10 minutes
      maxRetries: config.maxRetries || 2,
      debug: config.debug || false,
      ...config
    }

    // Create request queue to handle rate limiting
    this.queue = new PQueue({
      concurrency: 10, // Max concurrent requests
      interval: 60000, // 1 minute
      intervalCap: 60 // Max requests per minute
    })

    // Use global fetch or polyfill - bind to preserve 'this' context for browsers
    this.fetch = globalThis.fetch?.bind(globalThis)
    if (!this.fetch) {
      throw new Error('fetch is not available. Please provide a fetch polyfill.')
    }

    this.debug('SubscribeDevClient initialized', { baseUrl: this._config.baseUrl })
  }

  get config() {
    return this._config as Readonly<SubscribeDevClientConfig & {
      baseUrl: string
      authorizationUrl: string
      timeout: number
      maxRetries: number
    }>
  }

  get pricingUrl() {
    return `${this._config.baseUrl}/subscribe`
  }

  get authorizationUrl() {
    // If no tokens at all, use demo flow
    if (!this._config.accessToken && !this._config.apiKey) {
      return `${this._config.authorizationUrl}/demo`
    }
    // Otherwise use regular authorization flow
    return this._config.authorizationUrl
  }

  /**
   * Run a prediction
   * Note: Now returns completed predictions directly - no polling needed
   * 
   * @param model - The model to use for the prediction
   * @param options - Configuration options for the prediction
   * @param options.input - Input data (can contain prompt OR messages array with multimodal content)
   * @param options.response_format - Optional response format specification. Supports:
   *   - `{ type: 'json_object' }` - Returns a JSON object
   *   - `{ type: 'json_schema', json_schema: { name: string, schema: object } }` - Returns JSON matching the schema
   *   - `MyZodSchema` - Pass a Zod schema directly for automatic JSON schema conversion
   * @param options.wait - For compatibility - ignored since predictions complete synchronously
   * @returns Promise resolving to the prediction result
   * 
   * @example
   * ```typescript
   * // Basic usage with JSON object response
   * const result = await client.run('gpt-4', {
   *   input: { prompt: 'Hello world' },
   *   response_format: { type: 'json_object' }
   * });
   * 
   * // Using Zod schema directly
   * import { z } from 'zod';
   * const UserSchema = z.object({
   *   name: z.string(),
   *   age: z.number()
   * });
   * 
   * const result = await client.run('gpt-4', {
   *   input: { prompt: 'Generate a user' },
   *   response_format: UserSchema
   * });
   * ```
   */
  run(
    model: string,
    options: {
      input: Record<string, any>     // Can contain prompt OR messages array with multimodal content
      response_format?: ResponseFormat
      wait?: boolean                 // For compatibility - ignored since predictions complete synchronously
      stream?: boolean               // Enable streaming response
      signal?: AbortSignal           // Optional abort signal for cancellation
    },
    streamOptions?: {
      streamBy?: 'chunk' | 'word' | 'letter'  // How to yield streaming content
    }
  ): StreamableResponse<string> | Promise<any> {
    const { input, response_format, stream } = options
    const streamBy = streamOptions?.streamBy || 'chunk'

    // Generate request ID and emit start event
    const requestId = generateRequestId()
    const startTime = Date.now()

    this.emit('ai-call-start', {
      type: 'ai-call-start',
      timestamp: startTime,
      requestId,
      model,
      input,
      responseFormat: response_format,
      isStreaming: !!stream
    } as AICallEvent)

    // Force console log to ensure we see this regardless of debug setting
    console.log('[SubscribeDevClient] STREAMING DEBUG - Starting prediction', { 
      model, 
      input: typeof input, 
      response_format, 
      stream,
      streamFromOptions: options.stream,
      streamTruthy: !!stream,
      streamType: typeof stream,
      optionsKeys: Object.keys(options)
    })

    console.log('[SubscribeDevClient] STREAMING DEBUG - Stream parameter check', {
      stream,
      streamFromOptions: options.stream,
      streamTruthy: !!stream,
      optionsStream: 'stream' in options,
      streamValue: options.stream
    })

    // Make direct API call
    const requestPayload: any = {
      model,
      input: options.input
    }
    
    if (options.response_format) {
      requestPayload.response_format = this.processResponseFormat(options.response_format)
    }
    
    if (stream) {
      requestPayload.stream = true
      console.log('[SubscribeDevClient] STREAMING DEBUG - Added stream to payload', {
        stream,
        requestPayload
      })
    }
    
    console.log('[SubscribeDevClient] STREAMING DEBUG - Final request payload', {
      requestPayload,
      hasStream: 'stream' in requestPayload,
      streamValue: requestPayload.stream
    })
    
    console.log('[SubscribeDevClient] STREAMING DEBUG - Request payload stringified', {
      payloadString: JSON.stringify(requestPayload)
    })
    
    if (stream) {
      // Handle streaming response - return StreamableResponse directly (synchronously)
      console.log('[SubscribeDevClient] STREAMING DEBUG - Creating lazy streaming response')
      
      // Create async iterable that will handle the request lazily
      const self = this
      const lazyStreamingIterable = {
        [Symbol.asyncIterator]: async function* () {
          try {
            console.log('[SubscribeDevClient] STREAMING DEBUG - Starting lazy request execution')
            const streamingResult = await self.makeStreamingRequest('POST', '/v1/run', requestPayload, streamBy, options.signal)
            self.debug('Streaming request completed successfully:', {
              type: typeof streamingResult,
              hasAsyncIterator: !!(streamingResult && typeof streamingResult[Symbol.asyncIterator] === 'function'),
              isAsyncIterable: streamingResult instanceof Object && Symbol.asyncIterator in streamingResult
            })
            
            // Emit usage-changed event after successful streaming setup
            self.emit('usage-changed', { operation: 'run', model })

            let output: any[] = []
            // Yield from the actual streaming result
            for await (const chunk of streamingResult) {
              output.push(chunk)
              yield chunk
            }

            // Emit completion event after streaming finishes
            self.emit('ai-call-complete', {
              type: 'ai-call-complete',
              timestamp: Date.now(),
              requestId,
              model,
              input,
              output,
              duration: Date.now() - startTime,
              success: true,
              responseFormat: response_format,
              isStreaming: true
            } as AICallEvent)
          } catch (error) {
            self.debug('Streaming request failed:', {
              error: error instanceof Error ? error.message : String(error),
              errorType: error instanceof Error ? error.constructor.name : typeof error
            })

            // Emit error event
            self.emit('ai-call-error', {
              type: 'ai-call-error',
              timestamp: Date.now(),
              requestId,
              model,
              input,
              error: error instanceof Error ? error.message : String(error),
              duration: Date.now() - startTime,
              success: false,
              responseFormat: response_format,
              isStreaming: true
            } as AICallEvent)

            throw error
          }
        }
      }
      
      // Return StreamableResponse immediately (synchronously)
      return new StreamableResponse<string>(lazyStreamingIterable)
    } else {
      // Handle regular response - return Promise for non-streaming
      console.log('[SubscribeDevClient] STREAMING DEBUG - Making regular request (stream was false)', {
        stream,
        streamTruthy: !!stream,
        requestPayload
      })
      
      return (async () => {
        try {
          const response = await this.makeRequest('POST', '/v1/run', requestPayload, options.signal)

          this.debug('Prediction completed', {
            hasOutput: !!response.output
          })

          // Emit usage-changed event after successful run
          this.emit('usage-changed', { operation: 'run', model })

          if (this.isZodSchema(options?.response_format)) {
            try {
              const zodSchema = options.response_format as any;
              const responseObject = response.output?.[0] ?? '{}';
              response.output[0] = zodSchema.parse(typeof responseObject === 'string' ? JSON.parse(responseObject) : responseObject);
            } catch(err) {
              console.error("Could not parse incoming record to Zod schema: ", err, response.output[0]);
            }
          }

          // Emit completion event
          this.emit('ai-call-complete', {
            type: 'ai-call-complete',
            timestamp: Date.now(),
            requestId,
            model,
            input,
            output: response.output,
            duration: Date.now() - startTime,
            success: true,
            responseFormat: response_format,
            isStreaming: false
          } as AICallEvent)

          // Return just the output from the API
          return response
        } catch (error) {
          // Emit error event
          this.emit('ai-call-error', {
            type: 'ai-call-error',
            timestamp: Date.now(),
            requestId,
            model,
            input,
            error: error instanceof Error ? error.message : String(error),
            duration: Date.now() - startTime,
            success: false,
            responseFormat: response_format,
            isStreaming: false
          } as AICallEvent)

          throw error
        }
      })()
    }
  }

  /**
   * Process response format, converting Zod schemas to json_schema format
   */
  private processResponseFormat(responseFormat: ResponseFormat): ResponseFormat {
    // Handle native Zod schema passed directly
    if (this.isZodSchema(responseFormat)) {
      return this.convertZodSchema(responseFormat, 'native Zod schema')
    }
    
    // Return other formats as-is (json_object, json_schema, etc.)
    return responseFormat
  }

  /**
   * Check if the provided object looks like a Zod schema
   */
  private isZodSchema(obj: any): boolean {
    return obj && 
           typeof obj === 'object' && 
           (typeof obj._def === 'object' || typeof obj.parse === 'function') &&
           // Make sure it's not already a proper ResponseFormat object
           !obj.type
  }

  /**
   * Convert a Zod schema to json_schema format
   */
  private convertZodSchema(zodSchema: any, source: string): ResponseFormat {
    if (!zodSchema || typeof zodSchema !== 'object') {
      this.debug('Invalid Zod schema provided, falling back to json_object', { source })
      return { type: 'json_object' }
    }

    try {
      // Convert the OpenAI format to our ResponseFormat type
      return {
        type: 'json_schema',
        json_schema: {
          name: zodSchema._def?.typeName || 'ZodSchema',
          strict: true,
          schema: z.toJSONSchema(zodSchema)
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      this.debug('Failed to convert Zod schema, falling back to json_object', { error: errorMessage, source })
      // Fallback to json_object if conversion fails
      return { type: 'json_object' }
    }
  }

  /**
   * Get account balance information
   */
  async getBalance(): Promise<BalanceInfo> {
    const response = await this.makeRequest('GET', '/v1/subscriptions/usage')
    const userBalance = response.credits?.userBalance
    return {
      allocatedCredits: userBalance?.allocatedCredits || 0,
      usedCredits: userBalance?.usedCredits || 0,
      remainingCredits: userBalance?.remainingCredits || 0
    }
  }

  /**
   * Get user storage data using auth context
   */
  async getStorage(options: StorageOptions = {}): Promise<UserStorage> {
    const params = new URLSearchParams()
    
    if (options.appVersion) {
      params.set('appVersion', options.appVersion)
    }

    const url = `/v1/storage${params.toString() ? '?' + params.toString() : ''}`
    return this.makeRequest('GET', url)
  }

  /**
   * Update user storage data using auth context
   */
  async setStorage(
    sessionData: Record<string, any>, 
    options: StorageOptions = {}
  ): Promise<UserStorage> {
    const params = new URLSearchParams()
    
    if (options.appVersion) {
      params.set('appVersion', options.appVersion)
    }

    const url = `/v1/storage${params.toString() ? '?' + params.toString() : ''}`
    return this.makeRequest('PUT', url, { sessionData })
  }

  /**
   * Delete user storage data using auth context
   */
  async deleteStorage(options: StorageOptions = {}): Promise<void> {
    const params = new URLSearchParams()
    
    if (options.appVersion) {
      params.set('appVersion', options.appVersion)
    }

    const url = `/v1/storage${params.toString() ? '?' + params.toString() : ''}`
    await this.makeRequest('DELETE', url)
  }

  /**
   * Get user's subscription status using userKey authentication
   * Requires userKey to be provided in client configuration
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    console.log('[SubscribeDevClient] getSubscriptionStatus called')

    if (!this._config.userKey) {
      console.log('[SubscribeDevClient] No userKey provided, throwing ValidationError')
      throw new ValidationError(
        'User key is required to get subscription status. Provide userKey in client configuration.',
        { method: 'getSubscriptionStatus' }
      )
    }

    console.log('[SubscribeDevClient] Making request to /v1/subscriptions/plan with userKey:', this._config.userKey.substring(0, 10) + '...')

    try {
      const response = await this.makeRequest('GET', '/v1/subscriptions/plan')
      console.log('[SubscribeDevClient] Received response:', {
        hasUserPlan: !!response.userPlan,
        planId: response.userPlan?.planId,
        status: response.userPlan?.status,
        hasPlanDetails: !!response.userPlan?.plan
      })
      
      const userPlan = response.userPlan
      
      // Handle case where user has no subscription (status: 'none')
      if (!userPlan || userPlan.status === 'none' || !userPlan.plan) {
        console.log('[SubscribeDevClient] User has no active subscription, returning none status')
        return {
          hasActiveSubscription: false,
          status: userPlan?.status || 'none'
        }
      }

      const result = {
        hasActiveSubscription: userPlan.status === 'active',
        plan: userPlan.plan ? {
          id: userPlan.plan.id || userPlan.planId,
          name: userPlan.plan.name,
          price: userPlan.plan.price,
          tokenLimit: userPlan.plan.tokenLimit || userPlan.plan.token_limit,
          subtitle: userPlan.plan.subtitle,
          description: userPlan.plan.description,
          features: userPlan.plan.features
        } : undefined,
        status: userPlan.status,
        startedAt: userPlan.startedAt,
        endsAt: userPlan.endsAt
      }

      console.log('[SubscribeDevClient] Returning subscription statuSSSSs:', {
        hasActiveSubscription: result.hasActiveSubscription,
        status: result.status,
        planName: result.plan?.name
      })

      return result
    } catch (error: any) {
      console.log('[SubscribeDevClient] Error in getSubscriptionStatus:', {
        errorType: error.constructor.name,
        message: error.message
      })
      
      // Re-throw any genuine errors
      throw error
    }
  }

  /**
   * Get comprehensive usage limits and current consumption
   * Requires userKey to be provided in client configuration
   */
  async getUsageLimits(): Promise<UsageLimits> {
    if (!this._config.userKey) {
      throw new ValidationError(
          'User key is required to get usage limits. Provide userKey in client configuration.',
          { method: 'getUsageLimits' }
      )
    }
    return this.makeRequest('GET', '/v1/subscriptions/usage')
  }

  /**
   * Get comprehensive usage information including credits, rate limits, and consumption
   * Alias for getUsageLimits() - Requires userKey to be provided in client configuration
   */
  async getUsage(): Promise<UsageLimits> {
    return this.getUsageLimits()
  }

  /**
   * Get or create a demo project with a public API key
   * Requires userKey (userToken) to be provided in client configuration
   * The returned API key is public and safe to share
   */
  async getDemoProject(): Promise<DemoProject> {
    if (!this._config.userKey) {
      throw new ValidationError(
        'User key is required to get demo project. Provide userKey in client configuration.',
        { method: 'getDemoProject' }
      )
    }

    this.debug('Getting demo project')
    
    const response = await this.makePlatformRequest('POST', '/v1/platform/projects/demo')
    
    this.debug('Demo project retrieved', { 
      hasApiKey: !!response.apiKey,
      projectId: response.id,
      isDemo: response.isDemo
    });

    // Store the API key locally and log it
    const demoApiKey = response.apiKey
    console.log('Demo project API key (public and safe to share):', demoApiKey)

    return response
  }

  /**
   * Upload a file and get back the final public URL
   *
   * This is a convenience method that:
   * 1. Gets a presigned S3 URL from the API
   * 2. Uploads the file directly to S3
   * 3. Returns the final public download URL
   *
   * @param file - File object to upload (e.g., from input[type="file"])
   * @param options - Optional configuration (userId for organizing uploads)
   * @returns Promise resolving to the public S3 URL of the uploaded file
   *
   * @example
   * ```typescript
   * const fileInput = document.querySelector('input[type="file"]')
   * const file = fileInput.files[0]
   *
   * const imageUrl = await client.uploadFile(file)
   * console.log('Uploaded to:', imageUrl)
   * // => "https://bucket.s3.amazonaws.com/uploads/proj-123/user-456/photo.jpg"
   * ```
   */
  async uploadFile(file: File, options?: UploadFileOptions): Promise<string> {
    this.debug('Starting file upload', {
      filename: file.name,
      size: file.size,
      type: file.type,
      userId: options?.userId
    })

    // Step 1: Get presigned URL from API
    const params = new URLSearchParams()
    params.set('filename', file.name)
    params.set('contentType', file.type)

    if (options?.userId) {
      params.set('userId', options.userId)
    }

    const url = `/v1/upload-url?${params.toString()}`
    const uploadData = await this.makeRequest('GET', url) as UploadUrlResponse

    this.debug('Got presigned URL', {
      key: uploadData.key,
      expiresIn: uploadData.expiresIn
    })

    // Step 2: Upload file directly to S3 using presigned URL
    try {
      const s3Response = await this.fetch(uploadData.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        },
        signal: AbortSignal.timeout(this._config.timeout)
      })

      if (!s3Response.ok) {
        throw new Error(`S3 upload failed: ${s3Response.status} ${s3Response.statusText}`)
      }

      this.debug('File uploaded successfully', {
        downloadUrl: uploadData.downloadUrl
      })

      // Step 3: Return the final public download URL
      return uploadData.downloadUrl

    } catch (error) {
      this.debug('S3 upload failed', {
        error: error instanceof Error ? error.message : String(error)
      })
      throw new Error(
        `Failed to upload file to S3: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Make authenticated HTTP request to platform API using userToken as Bearer token
   */
  private async makePlatformRequest(
    method: string,
    path: string,
    body?: any
  ): Promise<any> {
    const url = `${this._config.baseUrl}${path}`
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${this._config.userKey}`,
      'Content-Type': 'application/json',
      'User-Agent': `@subscribe.dev/client/0.1.0`
    }

    const requestOptions: RequestInit = {
      method,
      headers,
      signal: AbortSignal.timeout(this._config.timeout)
    }

    if (body && ['POST', 'PUT', 'PATCH'].includes(method)) {
      requestOptions.body = JSON.stringify(body)
    }

    this.debug('Making platform request', { method, url, headers: { ...headers, Authorization: '[redacted]' } })

    const response = await this.fetch(url, requestOptions)
    
    // Handle response
    const responseText = await response.text()
    let responseData: any
    
    try {
      responseData = responseText ? JSON.parse(responseText) : null
    } catch {
      responseData = { error: { message: responseText } }
    }

    if (!response.ok) {
      this.handleErrorResponse(response, responseData)
    }

    this.debug('Platform request successful', { status: response.status, data: responseData })
    return responseData
  }

  /**
   * Make authenticated HTTP request to SubscribeDev API
   */
  private async makeRequest(
    method: string,
    path: string,
    body?: any,
    signal?: AbortSignal
  ): Promise<any> {
    return this.queue.add(async () => {
    const url = `${this._config.baseUrl}${path}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'User-Agent': `@subscribe.dev/client/0.1.0`
    }

    // Authentication priority:
    // 1. If accessToken provided -> ONLY use x-sub-dev-access-token (universal auth)
    // 2. If apiKey provided -> use Authorization Bearer + optional x-user-token
    if (this._config.accessToken) {
      // Universal authentication - accessToken handles both project and user context
      headers['x-sub-dev-access-token'] = this._config.accessToken
      this.debug('Using universal auth with x-sub-dev-access-token', {
        tokenPrefix: this._config.accessToken.substring(0, 10) + '...'
      });
    } else if (this._config.apiKey) {
      // Traditional project-based authentication with optional user context
      // Strip trailing = padding that sometimes appears
      const cleanApiKey = this._config.apiKey.trim().replace(/=+$/, '')
      headers['Authorization'] = `Bearer ${cleanApiKey}`
      this.debug('Using project auth with Authorization header');

      // Add user token if provided (for Clerk-based user context)
      if (this._config.userKey) {
        headers['X-User-Token'] = this._config.userKey
        this.debug('Adding X-User-Token for user context');
      }
    } else {
      this.debug('No authentication tokens available - unauthenticated request');
    }

    const requestOptions: RequestInit = {
      method,
      headers,
      signal: signal || AbortSignal.timeout(this._config.timeout)
    }

    if (body && ['POST', 'PUT', 'PATCH'].includes(method)) {
      requestOptions.body = JSON.stringify(body)
      this.debug('Regular request body stringified:', {
        originalBody: body,
        stringifiedBody: requestOptions.body,
        bodyContainsStream: JSON.stringify(body).includes('"stream"'),
        streamValue: body?.stream
      })
    }

    this.debug('Making request', { 
      method, 
      url, 
      body: body,
      hasStream: body?.stream,
      stringifiedBody: requestOptions.body,
      headers: { ...headers, Authorization: '[redacted]' } 
    })

    const response = await this.fetch(url, requestOptions)
    
    // Handle response
    const responseText = await response.text()
    let responseData: any
    
    try {
      responseData = responseText ? JSON.parse(responseText) : null
    } catch {
      responseData = { error: { message: responseText } }
    }

    if (!response.ok) {
      this.handleErrorResponse(response, responseData)
    }

    this.debug('Request successful', { status: response.status, data: responseData })
    return responseData
    })
  }

  /**
   * Make authenticated HTTP streaming request to SubscribeDev API
   */
  private async makeStreamingRequest(
    method: string,
    path: string,
    body?: any,
    streamBy: 'chunk' | 'word' | 'letter' = 'chunk',
    signal?: AbortSignal
  ): Promise<AsyncIterable<any>> {
    const url = `${this._config.baseUrl}${path}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'User-Agent': `@subscribe.dev/client/0.1.0`,
      'Accept': 'text/event-stream',
      'Cache-Control': 'no-cache'
    }

    // Authentication priority:
    // 1. If accessToken provided -> ONLY use x-sub-dev-access-token (universal auth)
    // 2. If apiKey provided -> use Authorization Bearer + optional x-user-token
    if (this._config.accessToken) {
      // Universal authentication - accessToken handles both project and user context
      headers['x-sub-dev-access-token'] = this._config.accessToken
      this.debug('Using universal auth with x-sub-dev-access-token for streaming', {
        tokenPrefix: this._config.accessToken.substring(0, 10) + '...'
      });
    } else if (this._config.apiKey) {
      // Traditional project-based authentication with optional user context
      // Strip trailing = padding that sometimes appears
      const cleanApiKey = this._config.apiKey.trim().replace(/=+$/, '')
      headers['Authorization'] = `Bearer ${cleanApiKey}`
      this.debug('Using project auth with Authorization header for streaming');

      // Add user token if provided (for Clerk-based user context)
      if (this._config.userKey) {
        headers['X-User-Token'] = this._config.userKey
        this.debug('Adding X-User-Token for user context in streaming');
      }
    } else {
      this.debug('No authentication tokens available for streaming - unauthenticated request');
    }

    const requestOptions: RequestInit = {
      method,
      headers,
      signal: signal || AbortSignal.timeout(this._config.timeout)
    }

    if (body && ['POST', 'PUT', 'PATCH'].includes(method)) {
      requestOptions.body = JSON.stringify(body)
      this.debug('Streaming request body stringified:', {
        originalBody: body,
        stringifiedBody: requestOptions.body,
        bodyContainsStream: JSON.stringify(body).includes('"stream"'),
        streamValue: body?.stream
      })
    }

    this.debug('Making streaming request', { 
      method, 
      url, 
      body: body,
      hasStream: body?.stream,
      stringifiedBody: requestOptions.body,
      headers: { ...headers, Authorization: '[redacted]' } 
    })

    console.log('[SubscribeDevClient] STREAMING DEBUG - About to fetch streaming request')
    const response = await this.fetch(url, requestOptions)

    console.log('[SubscribeDevClient] STREAMING DEBUG - Streaming response received:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries()),
      contentType: response.headers.get('content-type'),
      hasBody: !!response.body,
      bodyLocked: response.body?.locked,
      ok: response.ok
    })

    if (!response.ok) {
      const responseText = await response.text()
      let responseData: any
      
      try {
        responseData = responseText ? JSON.parse(responseText) : null
      } catch {
        responseData = { error: { message: responseText } }
      }

      this.handleErrorResponse(response, responseData)
    }

    if (!response.body) {
      console.log('[SubscribeDevClient] STREAMING DEBUG - No response body received')
      throw new Error('No response body received for streaming request')
    }

    console.log('[SubscribeDevClient] STREAMING DEBUG - Creating stream iterable from response body')
    
    // Clone the response body to avoid locking issues
    const [stream1, stream2] = response.body.tee()
    console.log('[SubscribeDevClient] STREAMING DEBUG - Response body teed for safe reading')
    
    const streamIterable = this.createStreamIterable(stream1, streamBy)
    console.log('[SubscribeDevClient] STREAMING DEBUG - Stream iterable created:', {
      type: typeof streamIterable,
      hasAsyncIterator: !!(streamIterable && typeof streamIterable[Symbol.asyncIterator] === 'function'),
      constructor: streamIterable.constructor.name
    })
    return streamIterable
  }

  /**
   * Create an async iterable from a ReadableStream for streaming responses
   */
  private createStreamIterable(responseBody: ReadableStream, streamBy: 'chunk' | 'word' | 'letter' = 'chunk'): AsyncIterable<string> {
    console.log('[SubscribeDevClient] STREAMING DEBUG - createStreamIterable called with streamBy:', streamBy)
    return {
      [Symbol.asyncIterator]: async function* () {
        const reader = responseBody.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let accumulatedText = ''

        try {
          while (true) {
            const { done, value } = await reader.read()
            
            if (done) {
              // Process any remaining buffer content
              if (buffer) {
                const lines = buffer.split('\n')
                for (const line of lines) {
                  if (line && line.startsWith('data: ') && line !== 'data: [DONE]') {
                    try {
                      const data = line.slice(6)
                      const chunk = JSON.parse(data)
                      const chunkContent = chunk?.content || ''
                      if (chunkContent) {
                        accumulatedText += chunkContent
                      }
                    } catch (error) {
                      console.warn('[SubscribeDevClient] Failed to parse remaining chunk:', line, error)
                    }
                  }
                }
                // Always yield final accumulated text regardless of mode
                if (accumulatedText) {
                  yield accumulatedText
                }
              }
              break
            }

            // Decode the chunk and add to buffer
            buffer += decoder.decode(value, { stream: true })
            
            // Process complete lines (SSE format: "data: {...}\n\n")
            const lines = buffer.split('\n\n')
            buffer = lines.pop() || '' // Keep incomplete line in buffer
            
            for (const line of lines) {
              if (!line || line === 'data: [DONE]') {
                continue
              }

              if (line.startsWith('data: ')) {
                try {
                  const data = line.slice(6) // Remove 'data: ' prefix
                  const chunk = JSON.parse(data)
                  const chunkContent = chunk?.content || ''

                  if (chunkContent) {
                    if (streamBy === 'chunk') {
                      // Simply append chunk.content and yield accumulator
                      accumulatedText += chunkContent
                      yield accumulatedText
                    } else if (streamBy === 'letter') {
                      // Append each character from chunk, yielding entire string each time
                      for (const char of chunkContent) {
                        accumulatedText += char
                        yield accumulatedText
                      }
                    } else if (streamBy === 'word') {
                      // Split chunk text by spaces and process each part
                      const parts = chunkContent.split(/(\s+)/) // Keep whitespace as separate elements
                      
                      for (let i = 0; i < parts.length; i++) {
                        const part = parts[i]
                        if (part) {
                          accumulatedText += part
                          
                          // If this part contains whitespace or punctuation, it completes a word
                          // OR if this is the last part in the chunk, always yield to prevent loss
                          if (/[\s\p{P}]/u.test(part) || i === parts.length - 1) {
                            yield accumulatedText
                          }
                        }
                      }
                    }
                  }
                } catch (error) {
                  console.warn('[SubscribeDevClient] Failed to parse streaming chunk:', line, error)
                }
              }
            }
          }
        } catch (error) {
          console.error('[SubscribeDevClient] Streaming error:', error)
          throw error
        } finally {
          // Always release the reader when done
          try {
            reader.releaseLock()
          } catch (e) {
            // Reader might already be released, ignore
          }
        }
      }
    }
  }

  /**
   * Handle error responses and throw appropriate error types
   */
  private handleErrorResponse(response: Response, data: any): never {
    const requestId = response.headers.get('x-request-id') || undefined
    const error = data?.error || {}
    const message = error.message || `HTTP ${response.status}`

    switch (response.status) {
      case 400:
        throw new ValidationError(message, error.details, requestId)
      case 401:
        throw new AuthenticationError(message, requestId)
      case 402:
        // Check error code to determine which 402 error type
        if (error.code === 'PLAN_UPGRADE_REQUIRED') {
          throw new PlanUpgradeRequiredError(
            message,
            error.context?.upgradeUrl,
            error.context?.currentPlan,
            error.context?.currentLimit,
            error.context?.required,
            requestId
          )
        }
        if (error.code === 'USER_UNSUBSCRIBED' || message.toLowerCase().includes('unsubscribed') || message.toLowerCase().includes('subscription')) {
          throw new UnsubscribedError(message, requestId)
        }
        throw new InsufficientBalanceError(
          message,
          error.details?.remainingCredits || error.context?.available,
          error.details?.requiredCredits || error.context?.required,
          requestId
        )
      case 403:
        throw new AccessDeniedError(message, requestId)
      case 404:
        throw new NotFoundError(message, requestId)
      case 429:
        throw new RateLimitError(
          message,
          error.details?.resetTime,
          error.details?.retryAfter,
          requestId
        )
      case 503:
        // Check if this is a workflow unavailable error
        if (error.code === 'WORKFLOW_UNAVAILABLE') {
          throw new WorkflowUnavailableError(message, requestId)
        }
        throw new SubscribeDevError(
          message,
          error.code || 'SERVICE_UNAVAILABLE',
          response.status,
          error.details || error.context,
          requestId
        )
      default:
        throw new SubscribeDevError(
          message,
          error.code || 'UNKNOWN_ERROR',
          response.status,
          error.details || error.context,
          requestId
        )
    }
  }

  /**
   * Debug logging
   */
  private debug(message: string, data?: any) {
    if (this._config.debug) {
      console.log(`[SubscribeDevClient] ${message}`, data || '')
    }
  }


  /**
   * Static method to create client (for convenience)
   */
  static create(config: SubscribeDevClientConfig): SubscribeDevClient {
    return new SubscribeDevClient(config)
  }
}